// Content products the marketer can request to have built on their behalf
// (NPJB-281). Distinct from `ContentAssetType` which tags individual uploaded
// files/links — these are the categorical "what kind of content do you need?"
// picks. Wire shape: snake_case strings, matching the backend Literal.

export type ContentProductKind =
  | 'whitepaper'
  | 'ebook'
  | 'infographic'
  | 'content_audit'
  | 'personal_blogs'
  | 'ondemand_webinar';

interface ContentProductMeta {
  kind: ContentProductKind;
  label: string;
  /** 2-3 letter uppercase code shown inside `ContentProductChip`. */
  short: string;
  /** CSS background — token reference or off-scale literal. */
  color: string;
}

// Single source of truth, mirroring the `ASSET_TYPES` array shape in
// asset-types.ts. Overlapping kinds (whitepaper / ebook / infographic)
// intentionally reuse the same short codes + colours as their `ASSET_TYPES`
// equivalents so a Whitepaper chip looks identical whether it represents an
// uploaded file, an asset link, or a requested product.
export const CONTENT_PRODUCTS: ContentProductMeta[] = [
  { kind: 'whitepaper', label: 'Whitepaper', short: 'WP', color: 'var(--dz-blue-muted)' },
  { kind: 'ebook', label: 'Ebook', short: 'EB', color: 'var(--dz-blue-muted)' },
  // Peach shade (TC1), matching the existing IG asset type.
  { kind: 'infographic', label: 'Infographic', short: 'IG', color: 'var(--dz-tone-peach-fg)' },
  { kind: 'content_audit', label: 'Content Audit', short: 'CA', color: 'var(--dz-charcoal)' },
  { kind: 'personal_blogs', label: 'Personal Blogs', short: 'PB', color: 'var(--dz-link-blue)' },
  {
    kind: 'ondemand_webinar',
    label: 'OnDemand Webinar',
    short: 'OW',
    color: 'var(--dz-bright-blue)',
  },
];

export const CONTENT_PRODUCT_OPTIONS: readonly ContentProductKind[] = CONTENT_PRODUCTS.map(
  (p) => p.kind,
);

const KIND_SET = new Set<string>(CONTENT_PRODUCT_OPTIONS);

/** Type guard / allow-list — guards FE state against unknown kinds returned by
 *  the BE (legacy data, future Literal expansion before FE deploy). */
export const isContentProductKind = (value: string): value is ContentProductKind =>
  KIND_SET.has(value);

export const getContentProductLabel = (kind: ContentProductKind): string =>
  CONTENT_PRODUCTS.find((p) => p.kind === kind)?.label ?? kind;

export const getContentProductShort = (kind: ContentProductKind): string =>
  CONTENT_PRODUCTS.find((p) => p.kind === kind)?.short ?? kind;

export const getContentProductColor = (kind: ContentProductKind): string =>
  CONTENT_PRODUCTS.find((p) => p.kind === kind)?.color ?? 'var(--dz-muted)';
